// Adaptive snapshot playout delay + bounded remote extrapolation.
//
// Diagnostics (existing telemetry — read before blaming netcode):
//   Client /clientmetrics per 8s window (frame-health.ts):
//     maxSnapGap high + normal maxDrawMs  → network / server delivery (this module)
//     maxDrawMs high                      → client render cost
//     maxFrameGap / maxLongTask high      → main-thread / GC stalls
//   Server Prometheus (/metrics):
//     warlocks_ws_send_drops_total        → trySend backpressure (snapshot gaps)
//     tick slips internal                 → sim + broadcast overran 16.7ms budget
//     tick slips external                 → GC / scheduler delayed the ticker

import { clamp } from "./util";

/** Nominal server snapshot interval at 20 Hz (ms). */
export const SNAPSHOT_INTERVAL_MS = 50;

export const INTERP_BASE_MS = 50;
export const INTERP_MIN_MS = 90;
export const INTERP_MAX_MS = 250;
export const INTERP_JITTER_K = 2.5;
/** Max playout-delay change per second (ms/s) — avoids camera/world clock jerks. */
export const INTERP_SMOOTH_MS = 60;
/** Max dead-reckoning past the newest snapshot (ms). */
export const EXTRAP_CAP_MS = 150;

const SNAP_GAP_EMA = 0.15;

/** Reset jitter state at round / reconnect boundaries. */
export function resetNetJitter(game) {
  game._snapGapMean = SNAPSHOT_INTERVAL_MS;
  game._snapGapJitter = 0;
  game._lastSnapRecv = 0;
  game._lastSnapSeq = 0;
  game._interpDelayTarget = 110;
  game.interpDelay = 110;
}

/** Called from onState when a snapshot arrives during play. */
export function onSnapshotArrival(game, recv, seq) {
  const gap = game._lastSnapRecv > 0 ? recv - game._lastSnapRecv : 0;
  game._lastSnapRecv = recv;

  if (gap > 10 && gap < 4000) {
    const delta = gap - game._snapGapMean;
    game._snapGapMean += SNAP_GAP_EMA * delta;
    game._snapGapJitter += SNAP_GAP_EMA * (Math.abs(delta) - game._snapGapJitter);
  }

  if (seq != null && seq > 0) {
    if (game._lastSnapSeq > 0 && seq > game._lastSnapSeq + 1) {
      const drops = seq - game._lastSnapSeq - 1;
      game._snapGapJitter += SNAP_GAP_EMA * drops * SNAPSHOT_INTERVAL_MS;
    }
    game._lastSnapSeq = seq;
  }

  game._interpDelayTarget = clamp(
    game._snapGapMean + INTERP_JITTER_K * game._snapGapJitter,
    INTERP_MIN_MS,
    INTERP_MAX_MS,
  );
}

/** Smooth interpDelay toward the jitter-derived target each frame. */
export function tickInterpDelay(game, dt) {
  const target = game._interpDelayTarget ?? 110;
  const maxStep = INTERP_SMOOTH_MS * dt;
  const diff = target - game.interpDelay;
  if (Math.abs(diff) <= maxStep) game.interpDelay = target;
  else game.interpDelay += Math.sign(diff) * maxStep;
}
